// ===========================================
// Calendar Event Detail Dialog
// ===========================================

import { useState, useEffect, useMemo } from 'react';
import { addDays, format, isSameDay, startOfDay } from 'date-fns';
import { useAuth } from '../../../contexts/useAuth';
import { effectiveStartWeek, toLocalDateString } from '../../../utils/dateUtils';
import { meetAndGreetEventTarget } from '../../../utils/meetAndGreetNotes';
import { NotesMissingChip } from '../../meetAndGreet/MeetAndGreetNotesList';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Chip,
  Alert,
  Stack,
  Tooltip,
  Skeleton,
  Divider,
  Paper,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { extraLessonApi, AvailableSlot } from '../../../api/booking.api';
import EditIcon from '@mui/icons-material/Edit';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PlaceIcon from '@mui/icons-material/Place';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ActionMenu, { ActionMenuItem } from '../../common/ActionMenu';
import { CalendarEvent, getEventTypeColor, TimeSlot } from '../../../api/hybridBooking.api';
import { CalendarDisplaySettings } from '../CalendarDisplaySettingsModal';
import { useLessonEnrollments } from '../../../hooks/useLessons';
import { familyContactsFor } from '../../../utils/familyContacts';
import { LocationRoomPicker } from '../lessonDetail';
import ChangeClinicTeacherDialog from '../hybridBooking/ChangeClinicTeacherDialog';
import {
  useAdminAvailableSlots,
  useAdminRescheduleBooking,
  useAdminChangeBookingRoom,
  useAdminCancelBooking,
} from '../../../hooks/useHybridBooking';
import {
  useAdminRescheduleMakeupSlot,
  useAdminRescheduleExtraLesson,
  useAdminChangeMakeupSlotRoom,
  useAdminChangeExtraLessonRoom,
} from '../../../hooks/useBooking';
import {
  useAdminCancelExtraLesson,
  extraLessonKeys,
  slotDateKey,
} from '../../../hooks/useExtraLessonRequests';
import { usePermissions } from '../../../hooks/usePermissions';
import { isTeacherViewPath } from '../../layout/effectiveView';
import SlotPicker from '../../booking/SlotPicker';
import WeekTilePicker from '../../booking/WeekTilePicker';
import { StudentAvatar } from '../../student';
import { getContrastTextColor } from '../../../styles/brandColors';

interface CalendarEventDialogProps {
  event: CalendarEvent | null;
  displaySettings: CalendarDisplaySettings;
  basePath: string;
  onClose: () => void;
}

export default function CalendarEventDialog({
  event,
  displaySettings,
  basePath,
  onClose,
}: CalendarEventDialogProps) {
  const { can } = usePermissions();
  // Roles 1b §5.15: the edit actions follow the server's gate (`scheduling.write`), and the
  // teacher view never offers them, whoever is looking (basePath is `…/teacher` there).
  const mayEditSchedule = can('scheduling', 'write') && !isTeacherViewPath(basePath);

  // A Meet & Greet event (#255) opens its own page: the teacher's notes page for the booking's
  // teacher, the admin Meet & Greet detail for a viewer with `meet_and_greet.read`.
  const { user: authUser } = useAuth();
  const isMeetAndGreet = event?.resource?.type === 'MEET_AND_GREET';
  const meetAndGreetTarget = isMeetAndGreet
    ? meetAndGreetEventTarget({
        bookingId: event?.resource?.bookingId,
        eventTeacherId: event?.resource?.teacherId,
        basePath,
        isTeacherView: isTeacherViewPath(basePath),
        myTeacherId: authUser?.teacherId,
        canReadMeetAndGreet: can('meet_and_greet', 'read'),
      })
    : null;

  const lessonId = event?.resource?.lessonId ?? '';
  const isIndividual = event?.resource?.type === 'INDIVIDUAL' || event?.resource?.type === 'HYBRID_INDIVIDUAL';
  const isHybridIndividual = event?.resource?.type === 'HYBRID_INDIVIDUAL';
  const isMakeup = event?.resource?.type === 'MAKEUP';
  const isExtraLesson = event?.resource?.type === 'EXTRA_LESSON';
  const isBooking = event?.resource?.isBooking === true;
  const bookingId = event?.resource?.bookingId;
  const weekNumber = event?.resource?.weekNumber ?? 0;
  // For MAKEUP this is MakeupSlot.id; for EXTRA_LESSON it's ExtraLessonRequest.id.
  // Both reschedule flows key off (slotId, teacherId, duration) + a chosen date/time,
  // so the picker UI below is shared.
  const makeupSlotId = event?.resource?.slotId;
  const makeupTeacherId = event?.resource?.teacherId;
  // HYBRID_INDIVIDUAL bookings carry studentName directly on the resource — no need to query enrollments
  const showEnrollments = !!lessonId && event?.resource?.type !== 'MEET_AND_GREET' && event?.resource?.type !== 'HYBRID_PLACEHOLDER' && !isHybridIndividual;
  const showStudents = showEnrollments || (isHybridIndividual && !!event?.resource?.studentName);

  const { data: enrollments, isLoading: enrollmentsLoading } = useLessonEnrollments(showEnrollments ? lessonId : '');
  // Only students who have STARTED by this occurrence's week (mid-term start week, Phase 4):
  // a joiner at week 7 is not in the room in week 5. No week on the event = no filter.
  const activeEnrollments =
    enrollments?.filter(
      (e) => e.isActive && (weekNumber <= 0 || effectiveStartWeek({}, e) <= weekNumber)
    ) ?? [];
  const hasNotStartedEnrollments =
    weekNumber > 0 && !!enrollments?.some((e) => e.isActive && effectiveStartWeek({}, e) > weekNumber);
  // Deliberately NOT reset per event: an admin who turns parents on keeps
  // seeing them as they click through classes.
  const [showParents, setShowParents] = useState(false);

  // Edit mode state (admin reschedule of a hybrid booking)
  const [editMode, setEditMode] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  // Admins may move a 1:1 clinic into ANY week of the term, not just its booked
  // week. Defaults to the booking's current week when edit mode opens.
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [cancelMode, setCancelMode] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // Defaults to ON: a cancellation is something the family must hear about, so
  // silence is a deliberate choice. The opt-out is for an admin undoing their
  // OWN mistaken booking, which from the family's side never existed.
  //
  // ⚠️ This initial value is NOT the effective default — the reset `useEffect`
  // below fires on mount and re-sets it. Change BOTH, or the one you edited will
  // appear to do nothing. (Verified: flipping only this line leaves the UI
  // unchanged and every test green; flipping the effect fails all 7.)
  const [cancelNotify, setCancelNotify] = useState(true);

  // Admin makeup reschedule (single-day picker with availability flags)
  const [makeupDate, setMakeupDate] = useState<Date | null>(null);
  const [makeupTime, setMakeupTime] = useState<string>('');

  // Per-instance Change Location — an inline panel in this modal. Shared across
  // the three per-instance event kinds (1:1 clinic booking, make-up slot, extra
  // lesson); the discriminator decides which mutation fires on confirm.
  const isClinicBooking = isHybridIndividual && isBooking && !!bookingId;
  const [clinicRoomMode, setClinicRoomMode] = useState(false);
  const [roomChangeKind, setRoomChangeKind] = useState<'clinic' | 'makeup' | 'extra'>('clinic');
  const [clinicRoomId, setClinicRoomId] = useState<string>('');
  const [clinicReason, setClinicReason] = useState('');
  const [clinicNotify, setClinicNotify] = useState(true);
  const [clinicOverride, setClinicOverride] = useState(false);
  const changeBookingRoom = useAdminChangeBookingRoom();
  // Per-booking Change Teacher (2026-09-06) — a separate dialog shared with the
  // student bookings list and the class status grid.
  const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
  const changeMakeupRoom = useAdminChangeMakeupSlotRoom();
  const changeExtraRoom = useAdminChangeExtraLessonRoom();

  const canEditBooking = mayEditSchedule && isBooking && !!bookingId && weekNumber > 0;
  const canRescheduleMakeup =
    mayEditSchedule && isMakeup && !!makeupSlotId && !!makeupTeacherId;
  const canRescheduleExtraLesson =
    mayEditSchedule && isExtraLesson && !!makeupSlotId && !!makeupTeacherId;
  // Change Location is offered on a booked make-up slot or scheduled extra lesson
  // (makeupSlotId carries the slot/request id; a room must already exist to move from).
  const canChangeMakeupRoom = mayEditSchedule && isMakeup && !!makeupSlotId;
  const canChangeExtraRoom = mayEditSchedule && isExtraLesson && !!makeupSlotId;
  // Admin can cancel an extra lesson; makeupSlotId carries the requestId here.
  const canCancelExtraLesson = mayEditSchedule && isExtraLesson && !!makeupSlotId;
  // Either reschedule mode uses the teacher-day picker (date + time slots for one teacher).
  const usesTeacherDayPicker = canRescheduleMakeup || canRescheduleExtraLesson;

  // Duration of the existing makeup slot in minutes (defaults to 30)
  const makeupDuration = useMemo(() => {
    if (!event?.start || !event?.end) return 30;
    const ms = event.end.getTime() - event.start.getTime();
    return Math.max(15, Math.round(ms / 60000));
  }, [event?.start, event?.end]);

  const { data: availableSlots, isLoading: slotsLoading } = useAdminAvailableSlots(
    canEditBooking && editMode ? lessonId : '',
    canEditBooking && editMode ? selectedWeek : 0,
    canEditBooking && editMode ? bookingId : undefined
  );

  // Days the teacher is available (used to constrain the date picker)
  const { data: makeupAvailableDays = [], isFetching: makeupDaysLoading } = useQuery({
    queryKey: extraLessonKeys.adminAvailabilityDays(makeupTeacherId ?? ''),
    queryFn: () => extraLessonApi.adminGetTeacherAvailabilityDays(makeupTeacherId!),
    enabled: usesTeacherDayPicker && editMode && !!makeupTeacherId,
  });

  // Slots for the chosen day (with isAvailable + reason flags)
  const { data: makeupDaySlots = [], isFetching: makeupSlotsLoading } = useQuery<AvailableSlot[]>({
    // Same endpoint as extraLessonKeys.adminAvailableSlots, so use that factory
    // member — a bare literal sits outside extraLessonKeys.all and is missed by
    // every slot-cache invalidation (md/review/invalidation.md).
    // ⚠️ Key on the LOCAL YYYY-MM-DD the API actually queries, not toISOString():
    // adminGetAvailableSlots normalises a Date with LOCAL getters, so an ISO key
    // names a different day west of UTC and two entries hold the same response.
    queryKey: extraLessonKeys.adminAvailableSlots(
      makeupTeacherId ?? '',
      makeupDate ? slotDateKey(makeupDate) : undefined,
      makeupDuration,
    ),
    queryFn: () =>
      extraLessonApi.adminGetAvailableSlots(makeupTeacherId!, makeupDate!, makeupDuration),
    enabled: usesTeacherDayPicker && editMode && !!makeupTeacherId && !!makeupDate,
  });

  // Suggested dates: next 14 days that match the teacher's availability days
  const makeupSuggestedDates = useMemo<Date[]>(() => {
    if (!usesTeacherDayPicker || makeupAvailableDays.length === 0) return [];
    const dates: Date[] = [];
    let cursor = startOfDay(new Date());
    const max = addDays(cursor, 60);
    while (cursor <= max && dates.length < 14) {
      if (makeupAvailableDays.includes(cursor.getDay())) dates.push(cursor);
      cursor = addDays(cursor, 1);
    }
    return dates;
  }, [usesTeacherDayPicker, makeupAvailableDays]);

  // Find current booking slot in available slots
  // When editing, backend includes the current booking with isAvailable: false
  // We identify it by matching event start time (formatting varies by timezone handling)
  const currentSlot = useMemo(() => {
    if (!editMode || !availableSlots || !event?.start) return null;
    const eventStartTime = format(event.start, 'HH:mm');
    // event.start is a fake-local Date (reconstructed browser-local wall-clock). Its LOCAL
    // calendar day is what matches slot.dateKey; toISOString() here would shift the key to UTC
    // and miss the current slot for viewers where local time crosses the date line vs UTC.
    const eventDateKey = toLocalDateString(event.start);
    return availableSlots.find(
      (slot) =>
        slot.startTime === eventStartTime &&
        slot.dateKey === eventDateKey
    ) || null;
  }, [editMode, availableSlots, event?.start]);

  const rescheduleBooking = useAdminRescheduleBooking();
  const cancelBooking = useAdminCancelBooking();
  const rescheduleMakeup = useAdminRescheduleMakeupSlot();
  const rescheduleExtraLesson = useAdminRescheduleExtraLesson();
  const cancelExtraLesson = useAdminCancelExtraLesson();

  // Reset internal state when the event changes / dialog reopens
  useEffect(() => {
    setEditMode(false);
    setCancelMode(false);
    setSelectedSlot(null);
    setSelectedWeek(0);
    setCancelReason('');
    setCancelNotify(true);
    setMakeupDate(null);
    setMakeupTime('');
    setClinicRoomMode(false);
    setRoomChangeKind('clinic');
    setClinicRoomId(event?.resource?.roomId ?? '');
    setClinicReason('');
    setClinicNotify(true);
    setClinicOverride(false);
    setTeacherDialogOpen(false);
  }, [event?.id, event?.resource?.roomId]);

  if (!event) return null;

  const dialogTitle = isHybridIndividual && event.resource?.studentName
    ? `1:1 Clinic - ${event.resource.studentName}`
    : event.title;

  const handleClose = () => {
    setEditMode(false);
    setCancelMode(false);
    setSelectedSlot(null);
    setSelectedWeek(0);
    setCancelReason('');
    setCancelNotify(true);
    setMakeupDate(null);
    setMakeupTime('');
    setClinicRoomMode(false);
    onClose();
  };

  const handleConfirmReschedule = () => {
    if (!bookingId || !selectedSlot) return;
    // Build the wire date from the tz-stable dateKey (YYYY-MM-DD) so the
    // calendar day cannot shift through a JS Date round-trip in tz != UTC.
    const dateKey =
      selectedSlot.dateKey ??
      (selectedSlot.date ? new Date(selectedSlot.date).toISOString().split('T')[0] : '');
    rescheduleBooking.mutate(
      {
        id: bookingId,
        data: {
          scheduledDate: `${dateKey}T00:00:00.000Z`,
          startTime: selectedSlot.startTime,
          endTime: selectedSlot.endTime,
        },
      },
      { onSuccess: () => handleClose() }
    );
  };

  const handleConfirmMakeupReschedule = () => {
    if (!makeupSlotId || !makeupDate || !makeupTime) return;
    const dateKey = format(makeupDate, 'yyyy-MM-dd');
    const [h, m] = makeupTime.split(':').map(Number);
    const endMinutes = h * 60 + m + makeupDuration;
    const endTime = `${String(Math.floor(endMinutes / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
    rescheduleMakeup.mutate(
      {
        slotId: makeupSlotId,
        input: {
          date: `${dateKey}T00:00:00.000Z`,
          startTime: makeupTime,
          endTime,
        },
      },
      { onSuccess: () => handleClose() }
    );
  };

  const handleConfirmExtraLessonReschedule = () => {
    if (!makeupSlotId || !makeupDate || !makeupTime) return;
    const dateKey = format(makeupDate, 'yyyy-MM-dd');
    rescheduleExtraLesson.mutate(
      {
        requestId: makeupSlotId,
        input: {
          scheduledDate: `${dateKey}T00:00:00.000Z`,
          scheduledTime: makeupTime,
          duration: makeupDuration,
        },
      },
      { onSuccess: () => handleClose() }
    );
  };

  const handleConfirmCancel = () => {
    if (canCancelExtraLesson && makeupSlotId) {
      cancelExtraLesson.mutate(
        {
          requestId: makeupSlotId,
          reason: cancelReason || undefined,
          notifyParents: cancelNotify,
        },
        { onSuccess: () => handleClose() }
      );
      return;
    }
    if (!bookingId) return;
    cancelBooking.mutate(
      { id: bookingId, reason: cancelReason || undefined, notifyParents: cancelNotify },
      { onSuccess: () => handleClose() }
    );
  };

  const handleConfirmRoomChange = () => {
    if (!clinicRoomId || clinicRoomId === event?.resource?.roomId) return;
    const data = {
      roomId: clinicRoomId,
      overrideConflicts: clinicOverride,
      reason: clinicReason || undefined,
      notifyParents: clinicNotify,
    };
    if (roomChangeKind === 'clinic') {
      if (!bookingId) return;
      changeBookingRoom.mutate({ id: bookingId, data }, { onSuccess: () => handleClose() });
    } else if (roomChangeKind === 'makeup') {
      if (!makeupSlotId) return;
      changeMakeupRoom.mutate({ slotId: makeupSlotId, input: data }, { onSuccess: () => handleClose() });
    } else {
      // extra lesson — makeupSlotId carries the requestId for extra events
      if (!makeupSlotId) return;
      changeExtraRoom.mutate({ requestId: makeupSlotId, input: data }, { onSuccess: () => handleClose() });
    }
  };

  const roomChangePending =
    changeBookingRoom.isPending || changeMakeupRoom.isPending || changeExtraRoom.isPending;

  return (
    <>
    <Dialog open={!!event} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {dialogTitle}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {isHybridIndividual && event.resource?.lessonName && (
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Regular class: {event.resource.lessonName}
          </Typography>
        )}
        {event.resource && (
          <Grid container spacing={2}>
            {/* Class Type */}
            {event.resource.lessonTypeName && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Class Type
                </Typography>
                <Box>
                  <Chip
                    label={event.resource.lessonTypeName}
                    size="small"
                    sx={{
                      backgroundColor:
                        event.resource.lessonTypeColor ||
                        getEventTypeColor(event.resource.type, displaySettings.specialEventColors),
                      color:
                        event.resource.lessonTypeColor === '#FFCE00' ||
                        event.resource.type === 'BAND'
                          ? '#080808'
                          : '#fff',
                    }}
                  />
                </Box>
              </Grid>
            )}

            {/* Instrument */}
            {event.resource.instrumentName && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Instrument
                </Typography>
                <Box>
                  <Chip
                    label={event.resource.instrumentName}
                    size="small"
                    sx={{
                      backgroundColor: event.resource.instrumentColor || '#9DA5AF',
                      color: '#fff',
                    }}
                  />
                </Box>
              </Grid>
            )}

            {/* Program Level — the name a BY_PROGRAM_LEVEL invoice prints and the
                cell the class is priced from, so a level that disagrees with the
                class name is visible here, not first on a family's invoice. */}
            {event.resource.programLevelName && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Program Level
                </Typography>
                <Box>
                  <Chip
                    label={event.resource.programLevelName}
                    size="small"
                    sx={{
                      backgroundColor: event.resource.programLevelColor || '#9DA5AF',
                      color: getContrastTextColor(event.resource.programLevelColor || '#9DA5AF'),
                    }}
                  />
                </Box>
              </Grid>
            )}

            {/* Teacher — when this occurrence is covered by a replacement, show
                the original (struck through) plus the replacement, mirroring the
                lesson detail page. teacherName already holds the resolved
                (replacement) teacher for this week. */}
            {event.resource.teacherName && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Teacher
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                  {event.resource.isReplaced && event.resource.originalTeacherName && (
                    <Chip
                      label={event.resource.originalTeacherName}
                      size="small"
                      title="Original teacher (replaced this week)"
                      sx={{
                        backgroundColor: event.resource.originalTeacherColor || '#4580E4',
                        color: '#fff',
                        textDecoration: 'line-through',
                        opacity: 0.6,
                      }}
                    />
                  )}
                  <Chip
                    label={
                      event.resource.isReplaced && event.resource.weekNumber
                        ? `${event.resource.teacherName} · Wk ${event.resource.weekNumber}`
                        : event.resource.teacherName
                    }
                    size="small"
                    color={event.resource.isReplaced ? 'warning' : undefined}
                    title={event.resource.isReplaced ? 'Replacement teacher (this week)' : 'Teacher'}
                    sx={{
                      backgroundColor: event.resource.teacherColor || '#4580E4',
                      color: '#fff',
                    }}
                  />
                </Box>
              </Grid>
            )}

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Date & Time
              </Typography>
              <Typography>
                {event.start && format(event.start, 'EEE, d MMM yyyy')}
              </Typography>
              <Typography>
                {event.start && format(event.start, 'h:mm a')} -{' '}
                {event.end && format(event.end, 'h:mm a')}
              </Typography>
            </Grid>

            {event.resource.roomName && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Room
                </Typography>
                <Typography>{event.resource.roomName}</Typography>
              </Grid>
            )}

            {event.resource.locationName && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Location
                </Typography>
                <Typography>{event.resource.locationName}</Typography>
              </Grid>
            )}

            {event.resource.enrolledCount !== undefined && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Enrolled
                </Typography>
                <Typography>
                  {event.resource.enrolledCount} / {event.resource.maxStudents}
                </Typography>
              </Grid>
            )}

            {event.resource.weekNumber && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Week
                </Typography>
                <Typography>Week {event.resource.weekNumber}</Typography>
              </Grid>
            )}

            {/* Make-up detail — admin AND teacher (both use this dialog; the
                parent feed never carries these fields). Not gated on mayEditSchedule,
                and ANY teacher sees it, not only the booked one: a replacement
                teacher needs the note too (Andrew, 2026-09-28, D11). */}
            {isExtraLesson && event.resource.makeupForLessonName && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Make-up for
                </Typography>
                <Typography>{event.resource.makeupForLessonName}</Typography>
              </Grid>
            )}
            {isExtraLesson && event.resource.staffNote && (
              <Grid item xs={12}>
                <Alert severity="info" icon={false} sx={{ py: 0.5 }} data-testid="extra-staff-note">
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                    Staff note{event.resource.shareNoteWithFamily ? ' · shared with the family' : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {event.resource.staffNote}
                  </Typography>
                </Alert>
              </Grid>
            )}

            {isMeetAndGreet && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {event.resource.studentName && (
                    <Typography variant="body2">Child: {event.resource.studentName}</Typography>
                  )}
                  {event.resource.notesMissing && <NotesMissingChip />}
                </Stack>
              </Grid>
            )}

            {event.resource.type === 'HYBRID_PLACEHOLDER' && (
              <Grid item xs={12}>
                <Alert
                  severity={event.resource.bookingsOpen ? 'success' : 'warning'}
                  sx={{ mt: 1 }}
                >
                  {event.resource.bookingsOpen
                    ? 'Bookings are open for this week'
                    : 'Bookings are not yet open for this week'}
                </Alert>
              </Grid>
            )}

            {/* Students enrolled in this class */}
            {showStudents && (
              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    {isIndividual ? 'Student' : 'Students'}
                  </Typography>
                  {showEnrollments && activeEnrollments.length > 0 && (
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={showParents}
                          onChange={(e) => setShowParents(e.target.checked)}
                        />
                      }
                      label={<Typography variant="caption">Show parents</Typography>}
                    />
                  )}
                </Stack>
                {isHybridIndividual && event.resource?.studentName ? (
                  /* Hybrid individual booking: student is on the booking, not lesson enrollments */
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                    <StudentAvatar
                      avatar={null}
                      birthDate=""
                      firstName={event.resource.studentName.split(' ')[0]}
                      lastName={event.resource.studentName.split(' ').slice(1).join(' ')}
                      size={36}
                    />
                    <Typography variant="body2">{event.resource.studentName}</Typography>
                  </Stack>
                ) : enrollmentsLoading ? (
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    {[1, 2].map((i) => (
                      <Skeleton key={i} variant="circular" width={36} height={36} />
                    ))}
                  </Stack>
                ) : activeEnrollments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {hasNotStartedEnrollments ? 'No students have started yet' : 'No students enrolled'}
                  </Typography>
                ) : showParents ? (
                  /* Parents toggle on: one row per student with the family's contacts */
                  <Stack spacing={1} sx={{ mt: 0.5 }}>
                    {activeEnrollments.map((enrollment) => {
                      const parents = familyContactsFor(enrollment.Student);
                      return (
                        <Stack key={enrollment.id} direction="row" alignItems="center" spacing={1}>
                          <StudentAvatar
                            avatar={enrollment.Student.avatar}
                            birthDate={enrollment.Student.birthDate ?? ''}
                            firstName={enrollment.Student.firstName}
                            lastName={enrollment.Student.lastName}
                            size={36}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2">
                              {enrollment.Student.firstName} {enrollment.Student.lastName}
                            </Typography>
                            {parents.length > 0 ? (
                              parents.map((c) => (
                                <Typography
                                  key={c.name}
                                  variant="caption"
                                  color="text.secondary"
                                  component="div"
                                >
                                  {c.name}
                                  {c.phone && (
                                    <>
                                      {' · '}
                                      <Box component="a" href={`tel:${c.phone}`} sx={{ color: 'inherit' }}>
                                        {c.phone}
                                      </Box>
                                    </>
                                  )}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No parent contact
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      );
                    })}
                  </Stack>
                ) : isIndividual ? (
                  /* Individual lesson: show name + avatar inline */
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                    <StudentAvatar
                      avatar={activeEnrollments[0].Student.avatar}
                      birthDate={activeEnrollments[0].Student.birthDate ?? ''}
                      firstName={activeEnrollments[0].Student.firstName}
                      lastName={activeEnrollments[0].Student.lastName}
                      size={36}
                    />
                    <Typography variant="body2">
                      {activeEnrollments[0].Student.firstName} {activeEnrollments[0].Student.lastName}
                    </Typography>
                  </Stack>
                ) : (
                  /* Group lesson: avatars with name tooltip + name below */
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {activeEnrollments.map((enrollment) => (
                      <Tooltip
                        key={enrollment.id}
                        title={`${enrollment.Student.firstName} ${enrollment.Student.lastName}`}
                        arrow
                      >
                        <Stack alignItems="center" spacing={0.5} sx={{ cursor: 'default' }}>
                          <StudentAvatar
                            avatar={enrollment.Student.avatar}
                            birthDate={enrollment.Student.birthDate ?? ''}
                            firstName={enrollment.Student.firstName}
                            lastName={enrollment.Student.lastName}
                            size={36}
                          />
                          <Typography variant="caption" noWrap sx={{ maxWidth: 60 }}>
                            {enrollment.Student.firstName}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    ))}
                  </Stack>
                )}
              </Grid>
            )}

            {/* Admin: edit-booking mode (slot picker) */}
            {canEditBooking && editMode && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Reschedule to a new slot
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Admin reschedule bypasses the 24-hour notice and bookings-open checks.
                  You can move this clinic into any week of the term.
                </Alert>
                {event.resource?.termStartDate && event.resource?.termEndDate && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Week
                    </Typography>
                    <WeekTilePicker
                      termStartDate={event.resource.termStartDate}
                      termEndDate={event.resource.termEndDate}
                      selectedWeek={selectedWeek}
                      onWeekSelect={(week) => {
                        setSelectedWeek(week);
                        setSelectedSlot(null);
                      }}
                      currentWeek={weekNumber}
                    />
                  </>
                )}
                <SlotPicker
                  slots={availableSlots}
                  selectedSlot={selectedSlot}
                  onSlotSelect={setSelectedSlot}
                  isLoading={slotsLoading}
                  emptyMessage="No available slots for this week."
                  confirmationPrefix="New time:"
                  currentSlot={currentSlot}
                />
              </Grid>
            )}

            {/* Admin: per-instance Change Location (clinic / makeup / extra) */}
            {clinicRoomMode && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  {roomChangeKind === 'clinic'
                    ? 'Change clinic location'
                    : roomChangeKind === 'makeup'
                    ? 'Change make-up lesson location'
                    : 'Change extra lesson location'}
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {roomChangeKind === 'clinic'
                    ? 'Moves only this 1:1 clinic to a different studio/room. The group lesson and other clinics stay put. The booked family is notified.'
                    : 'Moves only this session to a different studio/room — the date and time stay the same. The booked family is notified.'}
                </Alert>
                <LocationRoomPicker
                  value={clinicRoomId}
                  onChange={setClinicRoomId}
                  currentRoomId={event.resource?.roomId}
                />
                <FormControlLabel
                  sx={{ mt: 1, display: 'block' }}
                  control={
                    <Switch checked={clinicNotify} onChange={(e) => setClinicNotify(e.target.checked)} />
                  }
                  label="Notify the booked family by email"
                />
                <FormControlLabel
                  sx={{ display: 'block' }}
                  control={
                    <Switch checked={clinicOverride} onChange={(e) => setClinicOverride(e.target.checked)} />
                  }
                  label="Move even if the room is in use (override)"
                />
                <TextField
                  label="Reason (optional)"
                  value={clinicReason}
                  onChange={(e) => setClinicReason(e.target.value.slice(0, 500))}
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mt: 1 }}
                />
              </Grid>
            )}

            {/* Admin: makeup / extra-lesson reschedule mode (shared picker) */}
            {usesTeacherDayPicker && editMode && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Reschedule {canRescheduleMakeup ? 'make-up lesson' : 'extra lesson'} with{' '}
                  {event.resource?.teacherName}
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Pick a new date and time for this teacher. The booked family will be
                  notified by email automatically.
                </Alert>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Available Dates
                </Typography>
                {makeupDaysLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : makeupSuggestedDates.length === 0 ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    No availability configured for this teacher.
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {makeupSuggestedDates.map((date) => {
                      const selected = makeupDate && isSameDay(makeupDate, date);
                      return (
                        <Paper
                          key={date.toISOString()}
                          onClick={() => {
                            setMakeupDate(date);
                            setMakeupTime('');
                          }}
                          sx={{
                            p: 1.5,
                            textAlign: 'center',
                            cursor: 'pointer',
                            minWidth: 64,
                            border: 2,
                            borderColor: selected ? 'primary.main' : 'divider',
                            bgcolor: selected ? 'primary.main' : 'background.paper',
                            color: selected ? 'white' : 'text.primary',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: selected ? 'primary.main' : 'action.hover',
                            },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ textTransform: 'uppercase', opacity: 0.8, display: 'block' }}
                          >
                            {format(date, 'EEE')}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {format(date, 'd')}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {format(date, 'MMM')}
                          </Typography>
                        </Paper>
                      );
                    })}
                  </Box>
                )}

                {makeupDate && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Available Times — {format(makeupDate, 'EEEE, d MMMM')}
                    </Typography>
                    {makeupSlotsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : makeupDaySlots.length === 0 ? (
                      <Alert severity="warning" sx={{ py: 0.5 }}>
                        No available slots on this day.
                      </Alert>
                    ) : (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, 1fr)',
                          gap: 0.5,
                        }}
                      >
                        {makeupDaySlots.map((slot) => {
                          const selected = makeupTime === slot.startTime;
                          return (
                            <Paper
                              key={slot.startTime}
                              onClick={() => {
                                if (slot.isAvailable) setMakeupTime(slot.startTime);
                              }}
                              elevation={0}
                              sx={{
                                py: 0.75,
                                px: 0.5,
                                textAlign: 'center',
                                cursor: slot.isAvailable ? 'pointer' : 'not-allowed',
                                opacity: slot.isAvailable ? 1 : 0.45,
                                border: 1.5,
                                borderColor: selected ? 'primary.main' : 'rgba(0,0,0,0.12)',
                                bgcolor: selected ? 'primary.main' : 'transparent',
                                color: selected ? 'white' : 'text.primary',
                                transition: 'all 0.2s',
                                '&:hover': slot.isAvailable
                                  ? {
                                      borderColor: 'primary.main',
                                      bgcolor: selected ? 'primary.main' : 'action.hover',
                                    }
                                  : {},
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                              >
                                {format(new Date(`2000-01-01T${slot.startTime}`), 'h:mm a')}
                              </Typography>
                              {!slot.isAvailable && (
                                <Typography
                                  variant="caption"
                                  sx={{ opacity: 0.7, fontSize: '0.6rem', display: 'block' }}
                                >
                                  {slot.reason === 'OVERLAPS' ? 'Overlaps' : 'Booked'}
                                </Typography>
                              )}
                            </Paper>
                          );
                        })}
                      </Box>
                    )}
                  </>
                )}
              </Grid>
            )}

            {/* Admin: cancel-booking confirmation */}
            {(canEditBooking || canCancelExtraLesson) && cancelMode && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {canCancelExtraLesson
                    ? 'Cancel this extra lesson? The slot will be released and any make-up credit used will be refunded.'
                    : 'Cancel this booking? The slot will become available again.'}
                </Alert>
                <TextField
                  label="Cancellation reason (optional)"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  inputProps={{ maxLength: 500 }}
                  helperText={
                    cancelNotify
                      ? 'Included in the email to the family.'
                      : undefined
                  }
                />
                <FormControlLabel
                  sx={{ mt: 1, display: 'block' }}
                  control={
                    <Switch
                      checked={cancelNotify}
                      onChange={(e) => setCancelNotify(e.target.checked)}
                    />
                  }
                  label="Notify the family by email"
                />
                {!cancelNotify && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {canCancelExtraLesson
                      ? 'The family will not be told, and will not learn that their make-up credit was refunded.'
                      : 'The family will not be told this session was cancelled.'}
                  </Alert>
                )}
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        {/* Default actions — Close + View Lesson stay visible; the rest collapse
            into ⋮ More to keep the footer from wrapping. */}
        {!editMode && !cancelMode && !clinicRoomMode && (
          <>
            <Button onClick={handleClose}>Close</Button>
            {event.resource?.lessonId && (
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                href={`${basePath}/classes/${event.resource.lessonId}`}
              >
                View Lesson
              </Button>
            )}
            {meetAndGreetTarget && (
              <Button variant="outlined" startIcon={<OpenInNewIcon />} href={meetAndGreetTarget}>
                {event.resource?.notesMissing ? 'Record notes' : 'Open Meet & Greet'}
              </Button>
            )}
            {(() => {
              const items: ActionMenuItem[] = [];
              if (canEditBooking) {
                items.push({
                  key: 'edit-booking',
                  label: 'Edit Booking',
                  icon: <EditIcon fontSize="small" />,
                  onClick: () => { setSelectedWeek(weekNumber); setEditMode(true); },
                });
              }
              if (mayEditSchedule && isClinicBooking) {
                items.push({
                  key: 'change-location-clinic',
                  label: 'Change Location',
                  icon: <PlaceIcon fontSize="small" />,
                  onClick: () => {
                    setRoomChangeKind('clinic');
                    setClinicRoomId(event.resource?.roomId ?? '');
                    setClinicRoomMode(true);
                  },
                });
                items.push({
                  key: 'change-teacher-clinic',
                  label: 'Change Teacher',
                  icon: <SwapHorizIcon fontSize="small" />,
                  onClick: () => setTeacherDialogOpen(true),
                });
              }
              if (canRescheduleMakeup || canRescheduleExtraLesson) {
                items.push({
                  key: 'reschedule',
                  label: 'Reschedule',
                  icon: <EditIcon fontSize="small" />,
                  onClick: () => setEditMode(true),
                });
              }
              if (canChangeMakeupRoom || canChangeExtraRoom) {
                items.push({
                  key: 'change-location-slot',
                  label: 'Change Location',
                  icon: <PlaceIcon fontSize="small" />,
                  onClick: () => {
                    setRoomChangeKind(canChangeMakeupRoom ? 'makeup' : 'extra');
                    setClinicRoomId(event.resource?.roomId ?? '');
                    setClinicRoomMode(true);
                  },
                });
              }
              // NOTE: no lesson-level "Change Location" here — regular lesson
              // occurrences manage their location on the lesson detail page
              // (reached via "View Lesson"). Only per-instance events (1:1 clinic,
              // makeup, extra) get an inline Change Location on this modal.
              if (canEditBooking || canCancelExtraLesson) {
                items.push({
                  key: 'cancel',
                  label: canCancelExtraLesson ? 'Cancel Lesson' : 'Cancel Booking',
                  icon: <EventBusyIcon fontSize="small" />,
                  onClick: () => setCancelMode(true),
                  destructive: true,
                });
              }
              return <ActionMenu items={items} ariaLabel="More booking actions" />;
            })()}
          </>
        )}

        {/* Edit-mode actions */}
        {editMode && (
          <>
            <Button onClick={() => {
              setEditMode(false);
              setSelectedSlot(null);
              setSelectedWeek(0);
              setMakeupDate(null);
              setMakeupTime('');
            }}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={
                canRescheduleMakeup
                  ? handleConfirmMakeupReschedule
                  : canRescheduleExtraLesson
                  ? handleConfirmExtraLessonReschedule
                  : handleConfirmReschedule
              }
              disabled={
                usesTeacherDayPicker
                  ? !makeupDate ||
                    !makeupTime ||
                    rescheduleMakeup.isPending ||
                    rescheduleExtraLesson.isPending
                  : !selectedSlot || rescheduleBooking.isPending
              }
            >
              {(canRescheduleMakeup
                ? rescheduleMakeup.isPending
                : canRescheduleExtraLesson
                ? rescheduleExtraLesson.isPending
                : rescheduleBooking.isPending)
                ? <CircularProgress size={20} />
                : 'Confirm Reschedule'}
            </Button>
          </>
        )}

        {/* Cancel-mode actions */}
        {cancelMode && (
          <>
            <Button onClick={() => { setCancelMode(false); setCancelReason(''); setCancelNotify(true); }}>
              Back
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmCancel}
              disabled={cancelBooking.isPending || cancelExtraLesson.isPending}
            >
              {cancelBooking.isPending || cancelExtraLesson.isPending ? (
                <CircularProgress size={20} />
              ) : (
                'Confirm Cancel'
              )}
            </Button>
          </>
        )}

        {/* Per-instance Change Location actions (clinic / makeup / extra) */}
        {clinicRoomMode && (
          <>
            <Button onClick={() => setClinicRoomMode(false)} disabled={roomChangePending}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmRoomChange}
              disabled={
                !clinicRoomId ||
                clinicRoomId === event.resource?.roomId ||
                roomChangePending
              }
            >
              {roomChangePending ? <CircularProgress size={20} /> : 'Change Location'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
    {/* Mounted only while open: the dialog fetches eligible teachers on mount. */}
    {isClinicBooking && bookingId && teacherDialogOpen && (
      <ChangeClinicTeacherDialog
        open
        onClose={() => setTeacherDialogOpen(false)}
        bookingId={bookingId}
        lessonId={lessonId}
        currentTeacherId={event.resource?.teacherId}
        currentTeacherName={event.resource?.teacherName}
        weekNumber={weekNumber || undefined}
        scheduledDate={event.dateKey ?? (event.start ? toLocalDateString(event.start) : undefined)}
        startTime={event.startTime ?? (event.start ? format(event.start, 'HH:mm') : undefined)}
        endTime={event.endTime ?? (event.end ? format(event.end, 'HH:mm') : undefined)}
        onChanged={handleClose}
      />
    )}
    </>
  );
}
